import type { LizenzHinweis, LizenzZustand } from '@fameacc/contracts';

/**
 * Was das Hinweisband zeigt — reine Rechnung ohne React, damit die Tabelle
 * „Zustand und Stufe gegen Text" ohne Rendern prüfbar ist. Spec
 * `docs/superpowers/specs/2026-09-29-lizenzierung-design.md`, Abschnitte 6.1,
 * 8 und 10.2; Vertrag `lizenzHinweisSchema` in `@fameacc/contracts`.
 *
 * **Die Rangfolge ist die Aussage, von oben nach unten, die erste trifft:**
 *
 * 1. **Betriebsfehler** — die Zeitmarke der Lizenzprüfung ist nicht schreibbar.
 *    Das ist kein Lizenzzustand (Spec 5.2 Regel 3); der Text nennt deshalb
 *    keine Lizenzlage, weder „ohne Lizenz" noch „Nur-Lese".
 * 2. **Nur-Lese** — `nurLesen` (die Installation sperrt das Schreiben) oder
 *    ein eben erhaltenes `503 LIZENZ_NUR_LESEN`. Der Text richtet sich nach
 *    dem Zustand.
 * 3. **Warnung** — der Zustand `warnung`: die Freischaltung läuft in höchstens
 *    sieben Tagen ab, geschrieben wird noch.
 * 4. **Nichts.**
 *
 * **Seit `LZ-scharf` (2026-10-13) gibt es keine Vorwarnung „Ohne Lizenz endet
 * das Schreiben am …" mehr:** Sie gehörte zur meldenden Stufe und ihrem
 * Verfallsdatum (Spec 10.2). In der scharfen Stufe ist ohne Lizenz sofort
 * Nur-Lese-Betrieb, und das sagt Punkt 2. Die Felder `meldendBis` und
 * `endetOhneLizenz` des Vertrags bleiben, tragen aber nichts mehr (`D-52`).
 */

/** Die Art des Bandes bestimmt Farbe und Rolle, nicht den Text. */
export type BandArt = 'betriebsfehler' | 'nurLesen' | 'warnung';

export interface Band {
  readonly art: BandArt;
  readonly text: string;
}

const SCHUTZ_NUR_LESEN =
  'Einsicht, Auswertung und Export bleiben erhalten; neue Buchungen sind gesperrt.';

/** Der Grund je Zustand, für den Nur-Lese-Hinweis. */
const GRUND: Readonly<Record<LizenzZustand, string>> = {
  ohne_lizenz: 'Es ist keine gültige Lizenz hinterlegt.',
  deaktiviert: 'Die Lizenz wurde deaktiviert.',
  ohne_freischaltung: 'Die Lizenz ist nicht freigeschaltet.',
  abgelaufen: 'Die Freischaltung der Lizenz ist abgelaufen.',
  // Diese zwei sperren nie; der Text steht nur, falls der Server trotzdem
  // `nurLesen` meldet (etwa nach einem `503 LIZENZ_NUR_LESEN` mitten im Wechsel).
  warnung: 'Die Freischaltung der Lizenz läuft ab.',
  aktiv: 'Die Lizenz ist nicht mehr gültig.',
};

/** „in 1 Tag" / „in 5 Tagen" / „heute". */
function restText(restTage: number | null): string {
  if (restTage === null) return 'in Kürze';
  if (restTage <= 0) return 'heute';
  return restTage === 1 ? 'in 1 Tag' : `in ${String(restTage)} Tagen`;
}

/**
 * Das Band zu einem Hinweis. `hinweis === null`: noch nicht (oder nicht)
 * geladen. Dann zeigt es nur, was der Fehlerweg erzwingt — `erzwungenNurLesen`
 * nach einem `503 LIZENZ_NUR_LESEN` —, sonst nichts: Ein Hinweis, der sich
 * nicht laden lässt, ist kein Grund, die Oberfläche zu stören.
 */
export function bandFuer(hinweis: LizenzHinweis | null, erzwungenNurLesen: boolean): Band | null {
  if (hinweis?.betriebsfehler === true) {
    return {
      art: 'betriebsfehler',
      text: 'Betriebsfehler: Die Datenbank ist nicht beschreibbar (Zeitmarke der Lizenzprüfung). Bitte den Systemstatus prüfen.',
    };
  }
  if (hinweis?.nurLesen === true || erzwungenNurLesen) {
    const grund = hinweis === null ? '' : `${GRUND[hinweis.zustand]} `;
    return { art: 'nurLesen', text: `Nur-Lese-Betrieb: ${grund}${SCHUTZ_NUR_LESEN}` };
  }
  if (hinweis === null) return null;
  if (hinweis.zustand === 'warnung') {
    return {
      art: 'warnung',
      text: `Die Freischaltung der Lizenz läuft ${restText(hinweis.restTage)} ab.`,
    };
  }
  return null;
}
